import React from "react";
import { List, ListItemButton, ListItemIcon, ListItemText, Box } from "@mui/material";

// Extracted verbatim from Inbox's own filter-list rendering (InboxSettings.jsx,
// the "All/Me/Active/Unassigned/Closed/Blocked Contacts/Chat with Members" rows)
// so every rail that wants Inbox's exact look (row chrome, hover, selected state,
// count badge) shares one implementation instead of each page hand-copying the
// same sx values -- Settings' new sidebar is the first other consumer, Reports'
// planned rebuild is meant to be the second. Zero behavior change from the
// original inline markup; see getCountBadgeSx's own history in this file for why
// it looks the way it does (2026-08-18/29 iteration notes).

// Shared unread/count badge style. width/height fixed equal (not minWidth) forces
// a true circle for 1-2 digit counts; px only kicks in once a capped label
// ("99+") needs to widen into a pill instead of clipping.
export const getCountBadgeSx = (label) => ({
  minWidth: 24,
  height: 24,
  px: String(label).length > 2 ? "4px" : 0,
  borderRadius: "999px",
  backgroundColor: "primary.main",
  color: "#fff",
  fontSize: "0.75rem",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

/**
 * A flat list of nav rows in Inbox's exact visual style — rounded floating
 * pills inset from the rail's edges, theme-aware hover/selected background
 * (MUI's ListItemButton hardcodes its own `&.Mui-selected` background at
 * higher specificity than a flat sx override, so the exact compound selector
 * is targeted here, same as the original), no border accent.
 *
 * @param {Array<{key: string|number, label: string, icon: React.ReactNode, selected?: boolean, count?: number, countLabel?: string, onClick?: () => void}>} items
 *   `icon` is a fully-built element (caller controls its own selected color/variant swap,
 *   same contract Inbox's own item-building code already uses). `count` (raw number) gates
 *   whether the badge shows at all; `countLabel` is what's actually displayed inside it
 *   (defaults to `count` as a string) -- kept separate so a caller can pass an already-
 *   formatted "99+"-style label (e.g. via formatBadgeCount) without breaking the `> 0` gate,
 *   which a formatted string would fail (`"99+" > 0` is false).
 */
const SidebarNavList = ({ items }) => (
  <List disablePadding>
    {items.map((item, index) => (
      <ListItemButton
        key={item.key}
        selected={item.selected}
        onClick={item.onClick}
        sx={{
          cursor: "pointer",
          borderRadius: "8px",
          transition: "background-color 0.12s ease",
          padding: "8px 10px",
          mx: 1,
          mt: index === 0 ? 1 : 0.5,
          mb: index === items.length - 1 ? 1 : 0,
          width: "auto",
          "&.Mui-selected": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
          },
          "&.Mui-selected:hover": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"),
          },
          "&:hover": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {item.icon}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            sx: { color: (theme) => theme.palette.text.primary, fontSize: "0.875rem" },
          }}
        />
        {item.count > 0 && (
          <Box sx={getCountBadgeSx(item.countLabel ?? item.count)}>{item.countLabel ?? item.count}</Box>
        )}
      </ListItemButton>
    ))}
  </List>
);

export default SidebarNavList;
